/*
  Recolours the ring around the signed-in account avatar from Shop purple to Sip flame.

  Background, because this looks stranger than it is. When a customer signs in with Shop,
  Shopify does not render a plain circular avatar. It renders a coin: the profile photo on
  the front inside a 2px ring, a coloured cylinder for the coin's edge, and a back face
  carrying the white Shop bag logo, which flips into view on interaction. All four parts
  read one variable, --shop-brand-color: #5433eb.

  That variable is declared on #shopify-element-wrapper, a div inside the component's own
  shadow root. No theme stylesheet can reach it. Custom properties do inherit through a
  shadow boundary, but a value inherited from the host loses to a declaration made on the
  element itself, and that is exactly the situation here. There is also no ::part for the
  signed-in avatar, only for the signed-out one.

  The shadow root is open, so the one remaining route is to adopt an extra stylesheet into
  it. We recolour only the two parts that are visible while the coin is at rest: the front
  ring and the disc sitting directly behind the photo. The coin's edge and its back face
  stay Shop purple deliberately, because the back face carries Shop's own logo and putting
  another company's mark in Sip's orange would read as a bug, not as branding.

  The colour is not written here. It comes in as --sip-account-ring, set on the
  shopify-account element in snippets/header-actions.liquid from the flame in the theme's
  colour palette, so the brand colour stays in one place.

  This leans on two of Shopify's internal class names. Shopify ships this component
  independently of the theme and can rename them, in which case these rules stop matching
  and the ring simply goes back to purple. That is the intended way for this to fail:
  nothing breaks and no layout moves, the colour just reverts.
*/

const RING_CSS = `
  .account-button--shop-customer .account-button__coin-front::after {
    box-shadow: inset 0 0 0 var(--ring-width, 2px) var(--sip-account-ring, #FF6139) !important;
  }

  .account-button--shop-customer .account-button__coin-front::before {
    background-color: var(--sip-account-ring, #FF6139) !important;
  }
`;

function paintCoin(host) {
  const root = host.shadowRoot;
  if (!root || root.sipCoinPainted) return;

  // Constructable stylesheets are the clean route. Where they are unavailable, a plain
  // style element appended to the shadow root does the same job.
  if (typeof CSSStyleSheet === 'function' && 'replaceSync' in CSSStyleSheet.prototype) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(RING_CSS);
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  } else {
    const style = document.createElement('style');
    style.textContent = RING_CSS;
    root.appendChild(style);
  }

  root.sipCoinPainted = true;
}

function scan() {
  document.querySelectorAll('shopify-account').forEach(paintCoin);
}

// The stylesheet is adopted into the shadow root, so it applies to the avatar whenever it
// renders. That means one pass after the element upgrades is enough, and we do not have to
// wait for the customer's sign-in state to come back.
if ('customElements' in window) {
  customElements.whenDefined('shopify-account').then(scan);
}

// Re-run when the theme editor swaps the header section out from under us.
document.addEventListener('shopify:section:load', scan);
